import React, { useState, useRef, useEffect, useMemo } from 'react';

import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  Modal,
  Pressable,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path } from 'react-native-svg';
import ReactDOM from 'react-dom';
import { FONTS } from '../../constants/config';
import { PageContainer, Breadcrumb, SingleDatePicker } from '../../components';
import { computePortalPosition } from '../../utils/portalPosition';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { kpisApi, talentKPILinksApi, talentProjectsApi, talentActivitiesApi, talentSpecializationsApi } from '../../services/apiService';
import api from '../../services/api';
import { KPIType, Frequency, Polarity } from '../../types';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useResponsive } from '../../hooks';
import { normalizeArabicNumbers } from '../../utils/rtl';

type RouteProps = RouteProp<MainStackParamList, 'AddTalentKPI'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronDownIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M6 9L12 15L18 9" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const CalendarIcon = ({ size = 16, color = '#6B7280' }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M8 2V6M16 2V6M3 10H21M5 4H19C20.1046 4 21 4.89543 21 6V20C21 21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V6C3 4.89543 3.89543 4 5 4Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

// ============================================================
// Dropdown Component
// ============================================================

interface DropdownOption {
  value: string;
  label: string;
}

interface DropdownProps {
  value: string;
  options: DropdownOption[];
  placeholder: string;
  onChange: (value: string) => void;
}

const Dropdown = ({ value, options, placeholder, onChange }: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonLayout, setButtonLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const buttonRef = useRef<View>(null);
  const { colors, isDark } = useTheme();
  const selectedOption = options.find(opt => opt.value === value);

  const dropdownTheme = {
    bg: colors.card,
    border: colors.border,
    text: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    icon: colors.textMuted,
    menuBg: colors.card,
    menuBorder: colors.border,
    itemBg: colors.card,
    itemActiveBg: isDark ? '#451A1A' : '#FEF2F2',
  };

  const handleOpen = () => {
    buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setButtonLayout({ x, y, width, height });
      setIsOpen(true);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = () => {
      setIsOpen(false);
    };
    // Follow the trigger on scroll/resize instead of closing (so the last
    // dropdown on a page can be scrolled into view).
    const reposition = () => {
      buttonRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
        setButtonLayout({ x, y, width, height });
      });
    };
    document.addEventListener('mouseup', handleClickOutside);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      document.removeEventListener('mouseup', handleClickOutside);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen]);

  const DROPDOWN_MAX_H = 200;
  const dropPos = computePortalPosition(buttonLayout, buttonLayout.width || 200, DROPDOWN_MAX_H);
  const portalContent = isOpen ? ReactDOM.createPortal(
    <View
      style={{
        position: 'fixed' as any,
        top: dropPos.top,
        left: dropPos.left,
        width: buttonLayout.width,
        backgroundColor: dropdownTheme.menuBg,
        borderWidth: 1,
        borderColor: dropdownTheme.menuBorder,
        borderRadius: 12,
        paddingVertical: 8,
        zIndex: 99999,
        elevation: 99999,
        maxHeight: 200,
      }}
      // @ts-ignore - web style
      onMouseDown={(e: any) => e.stopPropagation()}
    >
      <View style={{ boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)' } as any}>
        <ScrollView style={{ maxHeight: 200 }} nestedScrollEnabled>
          {options.map((option) => (
            <TouchableOpacity
              key={option.value}
              style={[
                styles.dropdownItem,
                { backgroundColor: dropdownTheme.itemBg },
                value === option.value && { backgroundColor: dropdownTheme.itemActiveBg }
              ]}
              onPress={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              <Text style={[
                styles.dropdownItemText,
                { color: dropdownTheme.text },
                value === option.value && styles.dropdownItemTextActive
              ]}>
                {option.label}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
    </View>,
    document.body
  ) : null;

  return (
    <View style={styles.dropdownContainer}>
      <TouchableOpacity
        ref={buttonRef as any}
        style={[
          styles.dropdownButton,
          { backgroundColor: dropdownTheme.bg, borderColor: dropdownTheme.border },
        ]}
        onPress={handleOpen}
      >
        <ChevronDownIcon size={16} color={dropdownTheme.icon} />
        <Text style={[
          styles.dropdownButtonText,
          { color: dropdownTheme.text },
          !selectedOption && { color: dropdownTheme.placeholder }
        ]}>
          {selectedOption?.label || placeholder}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

// ============================================================
// Content Type Resolver
// ============================================================

/**
 * Resolves the Django ContentType PK for a given model name.
 * Uses the /resolve-content-type/ endpoint. Falls back to extracting
 * the content_type from existing talent-kpi-links if available.
 */
async function resolveContentTypeId(modelName: string, objectId?: number): Promise<number> {
  // Strategy 1: Try the dedicated endpoint
  try {
    const response = await api.get('/resolve-content-type/', {
      params: { app_label: 'talents', model: modelName },
    });
    if (response.data?.id) return response.data.id;
  } catch {
    // Endpoint may not exist yet, fall through to strategy 2
  }

  // Strategy 2: Fetch existing links for this entity and extract content_type
  if (objectId) {
    try {
      const response = await api.get('/talent-kpi-links/', {
        params: { object_id: objectId, page_size: 1 },
      });
      const results = response.data?.results || response.data;
      if (Array.isArray(results) && results.length > 0 && results[0].content_type) {
        return results[0].content_type;
      }
    } catch {
      // Fall through
    }
  }

  throw new Error(`Could not resolve ContentType ID for model "${modelName}"`);
}

// ============================================================
// Helper: map route params to entity info
// ============================================================

type EntityType = 'specialization' | 'project' | 'activity' | 'student';

interface EntityInfo {
  entityType: EntityType;
  modelName: string;
  entityId: number;
  sourceType: string;
}

function getEntityInfo(params: {
  specializationId?: number;
  projectId?: number;
  activityId?: number;
  studentId?: number;
}): EntityInfo | null {
  if (params.activityId) {
    return { entityType: 'activity', modelName: 'talentactivity', entityId: params.activityId, sourceType: 'activity' };
  }
  if (params.projectId) {
    return { entityType: 'project', modelName: 'talentproject', entityId: params.projectId, sourceType: 'project' };
  }
  if (params.studentId) {
    return { entityType: 'student', modelName: 'talentstudent', entityId: params.studentId, sourceType: 'student' };
  }
  if (params.specializationId) {
    return { entityType: 'specialization', modelName: 'talentspecialization', entityId: params.specializationId, sourceType: 'specialization' };
  }
  return null;
}

// ============================================================
// Custom Date Picker (same as AddTalentActivityScreen)
// ============================================================
const AR_MONTHS = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
const EN_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const AR_DAYS_SHORT = ['أح', 'إث', 'ثل', 'أر', 'خم', 'جم', 'سب'];
const EN_DAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

function buildMonthCells(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells: { day: number; dateStr: string; isCurrentMonth: boolean }[] = [];
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = daysInPrev - i;
    const m = month === 0 ? 11 : month - 1;
    const y = month === 0 ? year - 1 : year;
    cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, dateStr: `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: true });
  }
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    const m = month === 11 ? 0 : month + 1;
    const y = month === 11 ? year + 1 : year;
    cells.push({ day: d, dateStr: `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`, isCurrentMonth: false });
  }
  return cells;
}

interface SingleDatePickerProps {
  value: string;
  onChange: (date: string) => void;
  minDate?: string;
  placeholder?: string;
}

// Legacy in-file picker — superseded by the shared components/SingleDatePicker. Unused.
const _LegacySingleDatePicker = ({ value, onChange, minDate, placeholder }: SingleDatePickerProps) => {
  const { colors, isDark } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const [isOpen, setIsOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState<'days' | 'months' | 'years'>('days');
  const [layout, setLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const btnRef = useRef<View>(null);

  const initMonth = value ? new Date(value) : new Date();
  const [viewYear, setViewYear] = useState(initMonth.getFullYear());
  const [viewMonth, setViewMonth] = useState(initMonth.getMonth());
  const minYear = minDate ? new Date(minDate).getFullYear() : new Date().getFullYear();
  const minMonthIndex = minDate ? new Date(minDate).getMonth() : new Date().getMonth();
  const [yearRangeStart, setYearRangeStart] = useState(minYear);

  const todayStr = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  }, []);

  const handleOpen = () => {
    if (isOpen) { setIsOpen(false); return; }
    const d = value ? new Date(value) : new Date();
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
    setYearRangeStart(minYear);
    setPickerMode('days');
    btnRef.current?.measureInWindow((x: number, y: number, w: number, h: number) => {
      setLayout({ x, y, width: w, height: h });
      setIsOpen(true);
    });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const handleSelect = (dateStr: string) => { onChange(dateStr); setIsOpen(false); };

  const months = isRTL ? AR_MONTHS : EN_MONTHS;
  const dayHeaders = isRTL ? AR_DAYS_SHORT : EN_DAYS_SHORT;
  const cells = buildMonthCells(viewYear, viewMonth);
  const currentYear = new Date().getFullYear();
  const yearRange = Array.from({ length: 12 }, (_, i) => yearRangeStart + i);

  const displayValue = value
    ? (() => { const [y, m, d] = value.split('-'); return `${d}/${m}/${y}`; })()
    : '';

  const renderHeader = () => {
    if (pickerMode === 'years') {
      return (
        <View style={dpStyles.monthNav}>
          <TouchableOpacity onPress={() => setYearRangeStart(yearRangeStart + 12)} style={dpStyles.navBtn}>
            <View style={{ transform: [{ rotate: '90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
          </TouchableOpacity>
          <Text style={[dpStyles.monthLabel, { color: colors.textPrimary }]}>{yearRangeStart} - {yearRangeStart + 11}</Text>
          <TouchableOpacity onPress={() => { if (yearRangeStart - 12 >= minYear) setYearRangeStart(yearRangeStart - 12); }} style={dpStyles.navBtn} disabled={yearRangeStart <= minYear}>
            <View style={{ transform: [{ rotate: '-90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
          </TouchableOpacity>
        </View>
      );
    }
    if (pickerMode === 'months') {
      return (
        <View style={dpStyles.monthNav}>
          <TouchableOpacity onPress={() => { setViewYear(viewYear + 1); }} style={dpStyles.navBtn}>
            <View style={{ transform: [{ rotate: '90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => setPickerMode('years')}>
            <Text style={[dpStyles.monthLabel, { color: '#a9211f' }]}>{viewYear}</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => { if (viewYear > minYear) setViewYear(viewYear - 1); }} style={dpStyles.navBtn} disabled={viewYear <= minYear}>
            <View style={{ transform: [{ rotate: '-90deg' }] }}><ChevronDownIcon size={14} color={viewYear <= minYear ? '#CBD5E1' : colors.textPrimary} /></View>
          </TouchableOpacity>
        </View>
      );
    }
    return (
      <View style={dpStyles.monthNav}>
        <TouchableOpacity onPress={nextMonth} style={dpStyles.navBtn}>
          <View style={{ transform: [{ rotate: '90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
        </TouchableOpacity>
        <TouchableOpacity onPress={() => setPickerMode('months')}>
          <Text style={[dpStyles.monthLabel, { color: '#a9211f' }]}>{months[viewMonth]} {viewYear}</Text>
        </TouchableOpacity>
        <TouchableOpacity onPress={prevMonth} style={dpStyles.navBtn}>
          <View style={{ transform: [{ rotate: '-90deg' }] }}><ChevronDownIcon size={14} color={colors.textPrimary} /></View>
        </TouchableOpacity>
      </View>
    );
  };

  const renderBody = () => {
    if (pickerMode === 'years') {
      return (
        <View style={dpStyles.pickerGrid}>
          {yearRange.map(yr => {
            const isDisabled = yr < minYear;
            return (
              <TouchableOpacity key={yr} disabled={isDisabled} style={[dpStyles.pickerCell, yr === viewYear && { backgroundColor: '#a9211f', borderRadius: 8 }, yr === currentYear && yr !== viewYear && { backgroundColor: isDark ? '#333' : '#F1F5F9', borderRadius: 8 }]} onPress={() => { setViewYear(yr); setPickerMode('months'); }}>
                <Text style={[dpStyles.pickerCellText, { color: isDisabled ? '#CBD5E1' : colors.textPrimary }, yr === viewYear && { color: '#FFFFFF', fontFamily: FONTS.medium }]}>{yr}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      );
    }
    if (pickerMode === 'months') {
      return (
        <View style={dpStyles.pickerGrid}>
          {months.map((m, idx) => {
            const isDisabled = viewYear === minYear && idx < minMonthIndex;
            return (
              <TouchableOpacity key={idx} disabled={isDisabled} style={[dpStyles.pickerCell, idx === viewMonth && { backgroundColor: '#a9211f', borderRadius: 8 }]} onPress={() => { setViewMonth(idx); setPickerMode('days'); }}>
                <Text style={[dpStyles.pickerCellText, { color: isDisabled ? '#CBD5E1' : colors.textPrimary }, idx === viewMonth && { color: '#FFFFFF', fontFamily: FONTS.medium }]}>{m}</Text>
              </TouchableOpacity>
            );
          })}
        </View>
      );
    }
    return (
      <>
        <View style={dpStyles.dayHeaderRow}>
          {dayHeaders.map(h => <Text key={h} style={[dpStyles.dayHeaderText, { color: colors.textMuted }]}>{h}</Text>)}
        </View>
        <View style={dpStyles.grid}>
          {cells.map((cell, idx) => {
            const isSelected = cell.dateStr === value;
            const isToday = cell.dateStr === todayStr;
            const isDisabled = !cell.isCurrentMonth || (minDate ? cell.dateStr < minDate : false);
            return (
              <TouchableOpacity key={idx} style={[dpStyles.cell, isSelected && { backgroundColor: '#a9211f', borderRadius: 20 }, isToday && !isSelected && { backgroundColor: isDark ? '#333' : '#F1F5F9', borderRadius: 20 }]} onPress={() => !isDisabled && handleSelect(cell.dateStr)} disabled={isDisabled}>
                <Text style={[dpStyles.cellText, { color: isDisabled ? '#CBD5E1' : colors.textPrimary }, isSelected && { color: '#FFFFFF', fontFamily: FONTS.medium }]}>{cell.day}</Text>
              </TouchableOpacity>
              );
            })}
          </View>
        </>
      );
    };

  const portalContent = isOpen ? ReactDOM.createPortal(
    <>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 } as any} onClick={() => setIsOpen(false)} />
      <div style={{ position: 'fixed', top: layout.y + layout.height + 4, left: Math.max(8, layout.x + layout.width - 320), zIndex: 99999 } as any}>
        <View style={[dpStyles.panel, { backgroundColor: colors.card, borderColor: colors.border }]}>
          {renderHeader()}
          {renderBody()}
        </View>
      </div>
    </>,
    document.body,
  ) : null;

  return (
    <View ref={btnRef}>
      <TouchableOpacity onPress={handleOpen} style={[dpStyles.button, { backgroundColor: isDark ? colors.backgroundAlt : '#F9FAFB', borderColor: colors.border }]}>
        <CalendarIcon size={16} color={colors.textMuted} />
        <Text style={[dpStyles.buttonText, { color: value ? colors.textPrimary : colors.textMuted }]}>
          {displayValue || placeholder || (isRTL ? 'اختر التاريخ' : 'Select date')}
        </Text>
      </TouchableOpacity>
      {portalContent}
    </View>
  );
};

const dpStyles = StyleSheet.create({
  button: { flexDirection: 'row', alignItems: 'center', gap: 8, borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10 },
  buttonText: { fontSize: 14, fontFamily: FONTS.regular, flex: 1, textAlign: 'right' },
  panel: { width: 320, borderRadius: 12, borderWidth: 1, padding: 16, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 12, elevation: 10 },
  monthNav: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  navBtn: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  monthLabel: { fontSize: 16, fontFamily: FONTS.medium },
  dayHeaderRow: { flexDirection: 'row', marginBottom: 8 },
  dayHeaderText: { flex: 1, textAlign: 'center', fontSize: 12, fontFamily: FONTS.medium },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: { width: '14.28%' as any, alignItems: 'center', justifyContent: 'center', paddingVertical: 8 },
  cellText: { fontSize: 14, fontFamily: FONTS.regular },
  pickerGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 4 },
  pickerCell: { width: '30%' as any, alignItems: 'center', justifyContent: 'center', paddingVertical: 12, marginHorizontal: '1.5%' as any },
  pickerCellText: { fontSize: 14, fontFamily: FONTS.regular },
});

// ============================================================
// Main Component
// ============================================================

export default function AddTalentKPIScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { specializationId, projectId, activityId, studentId } = route.params || {};
  const { showSuccess, showError } = useNotification();
  const { isMobile } = useResponsive();
  const { colors, isDark } = useTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const entityInfo = getEntityInfo({ specializationId, projectId, activityId, studentId });

  // Today's date string for minDate on first selection
  const todayStr = useMemo(() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  }, []);

  // Track the first-ever selected start date — minDate reverts to this when changing
  const [firstStartDate, setFirstStartDate] = useState<string | null>(null);
  const startDateMinDate = firstStartDate || todayStr;

  // Theme colors for dark mode support
  const themeColors = {
    mainCard: colors.card,
    mainCardBorder: colors.cardBorder,
    pageTitle: colors.textPrimary,
    section: isDark ? colors.backgroundAlt : '#F1F5F9',
    sectionBorder: colors.border,
    sectionTitle: colors.textPrimary,
    label: colors.textPrimary,
    input: colors.card,
    inputBorder: colors.border,
    inputText: colors.textPrimary,
    placeholder: isDark ? '#6B7280' : '#9CA3AF',
    iconColor: colors.textMuted,
    cancelButton: colors.card,
    cancelButtonBorder: colors.border,
    cancelButtonText: colors.textMuted,
    checkboxBorder: isDark ? '#4B5563' : '#D1D5DB',
    checkboxBg: colors.card,
    dateInput: colors.card,
    dateInputBorder: colors.border,
  };

  // ============================================================
  // Form state
  // ============================================================
  const [formData, setFormData] = useState({
    // Section 1: Basic Information
    name: '',
    weight: '0',
    description: '',
    responsible: '',
    isPublicKPI: false,
    // Section 2: Classification
    kpiType: '' as KPIType | '',
    polarity: '' as Polarity | '',
    // Section 3: Measurement
    startDate: '',
    endDate: '',
    unit: '',
    unitDescription: '',
    frequency: '' as Frequency | '',
    dataSource: '',
    formula: '',
    baseline: '',
    baseYear: '',
    targetPattern: '',
    aggregationMethod: '',
    targetFirstValue: '',
    targetStepPercent: '',
    targetStepAmount: '',
  });

  // Section 4: Annual Targets — dynamic based on date range
  const [annualTargets, setAnnualTargets] = useState<Record<number, { reading: string; year: string }>>({})

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [usedWeight, setUsedWeight] = useState(0);

  // Fetch existing weights for this entity
  // For specializations: sum of KPI weights + project weights + activity weights = 100%
  // For other entities: sum of KPI weights only = 100%
  interface WeightItem { id: number; name: string; type: 'kpi' | 'project' | 'activity'; weight: number; linkId?: number }
  const [weightBreakdown, setWeightBreakdown] = useState({ kpis: 0, projects: 0, activities: 0 });
  const [weightItems, setWeightItems] = useState<WeightItem[]>([]);
  const [specName, setSpecName] = useState('');
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [modalWeights, setModalWeights] = useState<Record<string, string>>({});
  const [savingWeights, setSavingWeights] = useState(false);

  const fetchWeights = async () => {
    if (!entityInfo) return;
    try {
      const ctResp = await api.get('/resolve-content-type/', { params: { model: entityInfo.modelName } });
      const ctId = ctResp.data?.id;
      const params: any = { object_id: entityInfo.entityId };
      if (ctId) params.content_type = ctId;
      const links = await talentKPILinksApi.list(params);
      const items = links?.results || links || [];
      const kpiTotal = items.reduce((sum: number, link: any) => sum + (parseFloat(link.kpi_weight) || 0), 0);

      const allItems: WeightItem[] = items.map((link: any) => ({
        id: link.kpi || link.id,
        name: `${link.kpi_name || ''}${isRTL ? ' (مؤشر أداء)' : ' (KPI)'}`,
        type: 'kpi' as const,
        weight: parseFloat(link.kpi_weight) || 0,
        linkId: link.id,
      }));

      let projTotal = 0;
      let actTotal = 0;

      if (entityInfo.entityType === 'specialization') {
        try {
          const projResp = await talentProjectsApi.list({ specialization: entityInfo.entityId, page_size: 200 });
          const projects = projResp?.results || projResp || [];
          for (const proj of projects) {
            const pw = parseFloat(proj.weight) || 0;
            projTotal += pw;
            allItems.push({ id: proj.id, name: `${proj.name}${isRTL ? ' (مشروع)' : ' (Project)'}`, type: 'project', weight: pw });
            try {
              const actResp = await talentActivitiesApi.list({ project: proj.id, page_size: 200 });
              const activities = actResp?.results || actResp || [];
              for (const a of activities) {
                const aw = parseFloat(a.weight) || 0;
                actTotal += aw;
                allItems.push({ id: a.id, name: `${a.name}${isRTL ? ' (نشاط)' : ' (Activity)'}`, type: 'activity', weight: aw });
              }
            } catch { /* silent */ }
          }
          // Fetch specialization name
          try {
            const specResp = await talentSpecializationsApi.get(entityInfo.entityId);
            if (specResp?.name) setSpecName(specResp.name);
          } catch { /* silent */ }
        } catch { /* silent */ }
      }

      setWeightItems(allItems);
      setWeightBreakdown({ kpis: kpiTotal, projects: projTotal, activities: actTotal });
      setUsedWeight(kpiTotal + projTotal + actTotal);
    } catch {
      // silent — assume 0
    }
  };

  useEffect(() => { fetchWeights(); }, [entityInfo?.entityId, entityInfo?.modelName]);
  const isSpecialization = entityInfo?.entityType === 'specialization';

  // Weight distribution modal helpers
  const currentWeight = parseFloat(formData.weight) || 0;
  const openWeightModal = () => {
    const wts: Record<string, string> = {};
    weightItems.forEach((item, i) => { wts[`${item.type}-${item.id}-${i}`] = String(item.weight); });
    setModalWeights(wts);
    setShowWeightModal(true);
  };
  const modalTotal = Object.values(modalWeights).reduce((sum, v) => { const n = parseFloat(v); return sum + (isNaN(n) ? 0 : n); }, 0) + (isNaN(currentWeight) ? 0 : currentWeight);
  const modalRemaining = Math.round((100 - modalTotal) * 100) / 100;
  const autoRedistribute = () => {
    const count = Object.keys(modalWeights).length + 1; // +1 for current KPI
    const perItem = Math.floor((100 / count) * 100) / 100;
    const wts: Record<string, string> = {};
    Object.keys(modalWeights).forEach(k => { wts[k] = String(perItem); });
    setModalWeights(wts);
    updateField('weight', String(perItem));
  };
  const handleSaveWeights = async () => {
    if (modalRemaining < 0) return;
    setSavingWeights(true);
    try {
      for (const item of weightItems) {
        const key = Object.keys(modalWeights).find(k => k.startsWith(`${item.type}-${item.id}-`));
        const newWeight = parseFloat(key ? modalWeights[key] : '0') || 0;
        if (Math.abs(newWeight - item.weight) < 0.01) continue; // skip unchanged
        if (item.type === 'kpi' && item.linkId) {
          await api.patch(`/kpis/${item.id}/`, { weight: newWeight });
        } else if (item.type === 'project') {
          await talentProjectsApi.update(item.id, { weight: newWeight });
        } else if (item.type === 'activity') {
          await talentActivitiesApi.update(item.id, { weight: newWeight });
        }
      }
      setShowWeightModal(false);
      await fetchWeights(); // refresh
    } catch {
      // silent
    } finally {
      setSavingWeights(false);
    }
  };

  const remainingWeight = Math.round((100 - usedWeight - currentWeight) * 100) / 100;
  const isWeightExceeded = remainingWeight < 0;

  // ============================================================
  // Dropdown options
  // ============================================================
  const kpiTypeOptions: DropdownOption[] = [
    { value: 'strategic', label: isRTL ? 'استراتيجي' : 'Strategic' },
    { value: 'operational', label: isRTL ? 'تشغيلي' : 'Operational' },
  ];

  const polarityOptions: DropdownOption[] = [
    { value: 'positive', label: isRTL ? 'تصاعدي' : 'Ascending' },
    { value: 'negative', label: isRTL ? 'تنازلي' : 'Descending' },
  ];

  const unitOptions: DropdownOption[] = [
    { value: '%', label: isRTL ? 'نسبة' : 'Percentage' },
    { value: 'عدد', label: isRTL ? 'عدد' : 'Count' },
  ];

  const frequencyOptions: DropdownOption[] = [
    { value: 'monthly', label: isRTL ? 'شهري' : 'Monthly' },
    { value: 'quarterly', label: isRTL ? 'ربع سنوي' : 'Quarterly' },
    { value: 'half_yearly', label: isRTL ? 'نصف سنوي' : 'Semi-Annual' },
    { value: 'yearly', label: isRTL ? 'سنوي' : 'Annual' },
  ];

  const baseYearOptions: DropdownOption[] = [
    { value: '2025', label: '2025' },
    { value: '2026', label: '2026' },
    { value: '2027', label: '2027' },
  ];

  const targetPatternOptions: DropdownOption[] = [
    { value: 'fixed', label: isRTL ? 'ثابت' : 'Fixed' },
    { value: 'custom', label: isRTL ? 'مخصص' : 'Custom' },
    { value: 'pct_increase', label: isRTL ? 'متزايد بنسبة' : 'Increase by %' },
    { value: 'pct_decrease', label: isRTL ? 'متناقص بنسبة' : 'Decrease by %' },
    { value: 'num_increase', label: isRTL ? 'متزايد بقيمة عددية' : 'Increase by amount' },
    { value: 'num_decrease', label: isRTL ? 'متناقص بقيمة عددية' : 'Decrease by amount' },
  ];

  const aggregationMethodOptions: DropdownOption[] = [
    { value: 'sum', label: isRTL ? 'مجموع القيم' : 'Sum of Values' },
    { value: 'last', label: isRTL ? 'قيمة آخر قراءة' : 'Last Reading Value' },
  ];

  // Which step input(s) the selected pattern needs
  const isPctPattern = formData.targetPattern === 'pct_increase' || formData.targetPattern === 'pct_decrease';
  const isNumPattern = formData.targetPattern === 'num_increase' || formData.targetPattern === 'num_decrease';
  const isStepPattern = isPctPattern || isNumPattern;

  // ============================================================
  // Dynamic years from date range
  // ============================================================
  const targetYears: number[] = (() => {
    if (!formData.startDate || !formData.endDate) return [];
    const startYear = new Date(formData.startDate + 'T00:00:00').getFullYear();
    const endYear = new Date(formData.endDate + 'T00:00:00').getFullYear();
    if (isNaN(startYear) || isNaN(endYear) || endYear < startYear) return [];
    const years: number[] = [];
    for (let y = startYear; y <= endYear; y++) years.push(y);
    return years;
  })();

  // Sync annualTargets when years change — add new, remove old
  useEffect(() => {
    setAnnualTargets(prev => {
      const next: Record<number, { reading: string; year: string }> = {};
      for (const y of targetYears) {
        next[y] = prev[y] || { reading: '', year: '' };
      }
      return next;
    });
  }, [targetYears.join(',')]);

  const computedTotalTarget = targetYears.reduce(
    (sum, y) => sum + (parseFloat(annualTargets[y]?.year) || 0), 0
  );

  // ============================================================
  // Validation
  // ============================================================
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = isRTL ? 'اسم المؤشر مطلوب' : 'KPI name is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = isRTL ? 'وصف المؤشر مطلوب' : 'KPI description is required';
    }
    if (!formData.kpiType) {
      newErrors.kpiType = isRTL ? 'تصنيف المؤشر مطلوب' : 'KPI classification is required';
    }
    if (!formData.polarity) {
      newErrors.polarity = isRTL ? 'اتجاه المؤشر مطلوب' : 'Polarity is required';
    }
    if (!formData.unit) {
      newErrors.unit = isRTL ? 'وحدة القياس مطلوبة' : 'Unit is required';
    }
    if (!formData.frequency) {
      newErrors.frequency = isRTL ? 'دورية القياس مطلوبة' : 'Frequency is required';
    }
    if (!formData.startDate) {
      newErrors.startDate = isRTL ? 'تاريخ بداية القياس مطلوب' : 'Start date is required';
    }
    if (!formData.endDate) {
      newErrors.endDate = isRTL ? 'تاريخ التحقق مطلوب' : 'End date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============================================================
  // Save handler
  // ============================================================
  const handleSave = async () => {
    if (!validateForm()) {
      showError(isRTL ? 'يرجى تعبئة الحقول المطلوبة' : 'Please fill required fields');
      return;
    }

    if (!entityInfo) {
      showError(isRTL ? 'لم يتم تحديد الكيان المرتبط' : 'No linked entity specified');
      return;
    }

    if (isWeightExceeded) {
      showError(isRTL
        ? isSpecialization
          ? 'مجموع أوزان المؤشرات والمشاريع والأنشطة يتجاوز 100% — يرجى تقليل الوزن'
          : 'مجموع الأوزان يتجاوز 100% — يرجى تقليل الوزن'
        : isSpecialization
          ? 'Total weight of KPIs, projects, and activities exceeds 100% — please reduce the weight'
          : 'Total weight exceeds 100% — please reduce the weight');
      return;
    }

    setLoading(true);
    try {
      // Single-step: create KPI + link via talent-kpi-links/create-with-kpi/
      await api.post('/talent-kpi-links/create-with-kpi/', {
        kpi_data: {
          name: formData.name.trim(),
          description: formData.description.trim(),
          kpi_type: formData.kpiType as KPIType,
          polarity: formData.polarity as Polarity,
          unit: formData.unit,
          unit_description: formData.unitDescription.trim(),
          frequency: formData.frequency as Frequency,
          weight: parseFloat(formData.weight) || 0,
          formula: formData.formula.trim(),
          data_source: formData.dataSource.trim(),
          baseline: formData.baseline || null,
          baseline_year: formData.baseYear || null,
          target_pattern: formData.targetPattern || 'fixed',
          target_aggregation: formData.aggregationMethod || 'sum',
          target_first_value: formData.targetFirstValue || null,
          target_step_percent: isPctPattern ? (formData.targetStepPercent || null) : null,
          target_step_amount: isNumPattern ? (formData.targetStepAmount || null) : null,
          measurement_start_date: formData.startDate || null,
          verification_end_date: formData.endDate || null,
          target_value: computedTotalTarget > 0 ? computedTotalTarget.toFixed(2) : '0.00',
          // Overall/general only applies on a specialization (fans to students).
          is_general: entityInfo.entityType === 'specialization' ? formData.isPublicKPI : false,
        },
        entity_model: entityInfo.modelName,
        entity_id: entityInfo.entityId,
        target_value: computedTotalTarget > 0 ? computedTotalTarget.toFixed(2) : '0.00',
      });

      showSuccess(isRTL ? 'تم إضافة مؤشر الأداء بنجاح' : 'KPI added successfully');
      navigation.goBack();
    } catch (error: any) {
      console.error('Error creating Talent KPI:', error);
      const errorMessage =
        error.response?.data?.detail
        || error.response?.data?.name?.[0]
        || error.message
        || (isRTL ? 'فشل في إضافة مؤشر الأداء' : 'Failed to add KPI');
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigation.goBack();
  };

  const updateField = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <PageContainer>
      <ScrollView style={styles.scrollView} contentContainerStyle={[styles.content, { padding: 24 }]}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: isRTL ? 'الرئيسية' : 'Home' },
            { label: isRTL ? 'مبادرة مواهب' : 'Talents Initiative', route: 'Talents' as any },
            { label: isRTL ? 'إضافة مؤشر أداء' : 'Add KPI' },
          ]}
        />

        {/* Main Card Container */}
        <View style={[styles.mainCard, { backgroundColor: themeColors.mainCard, borderColor: themeColors.mainCardBorder }]}>
          {/* Page Title */}
          <Text style={[styles.pageTitle, { color: themeColors.pageTitle }]}>
            {isRTL ? 'إضافة مؤشر أداء' : 'Add Performance Indicator'}
          </Text>

          {/* ============================================ */}
          {/* Section 1: Basic Information */}
          {/* ============================================ */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>
              {isRTL ? 'المعلومات الأساسية' : 'Basic Information'}
            </Text>

            {/* Row 1: KPI Name + Weight */}
            <View style={[styles.row, isMobile && { flexDirection: 'column' }]}>
              {/* Weight Field */}
              <View style={styles.fieldSmall}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'الوزن' : 'Weight'}
                  </Text>
                </View>
                <View style={styles.weightInputRow}>
                  <View style={styles.setWeightLabel}>
                    <Text style={styles.setWeightButtonText}>
                      {isRTL ? 'تعيين الوزن' : 'Set Weight'}
                    </Text>
                  </View>
                  <Text style={styles.percentSymbol}>%</Text>
                  <TextInput
                    style={[styles.weightInput, { backgroundColor: themeColors.input, borderColor: isWeightExceeded ? '#DC2626' : themeColors.inputBorder, color: themeColors.inputText }]}
                    value={formData.weight}
                    onChangeText={(v) => updateField('weight', normalizeArabicNumbers(v))}
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
                <Text style={{ fontSize: 12, fontFamily: FONTS.regular, color: isWeightExceeded ? '#DC2626' : '#64748B', textAlign: isRTL ? 'right' : 'left', marginTop: 4 }}>
                  {isRTL
                    ? `المتبقي: %${Math.max(0, Math.round(100 - usedWeight - currentWeight))} — المستخدم: %${Math.round(usedWeight + currentWeight)}`
                    : `Remaining: ${Math.max(0, Math.round(100 - usedWeight - currentWeight))}% — Used: ${Math.round(usedWeight + currentWeight)}%`}
                </Text>
                {isSpecialization && (weightBreakdown.projects > 0 || weightBreakdown.activities > 0) && (
                  <Text style={{ fontSize: 11, fontFamily: FONTS.regular, color: '#94A3B8', textAlign: isRTL ? 'right' : 'left', marginTop: 2 }}>
                    {isRTL
                      ? `(مؤشرات: %${Math.round(weightBreakdown.kpis)}، مشاريع: %${Math.round(weightBreakdown.projects)}، أنشطة: %${Math.round(weightBreakdown.activities)})`
                      : `(KPIs: ${Math.round(weightBreakdown.kpis)}%, Projects: ${Math.round(weightBreakdown.projects)}%, Activities: ${Math.round(weightBreakdown.activities)}%)`}
                  </Text>
                )}
                {isWeightExceeded && (
                  <Text style={{ fontSize: 12, fontFamily: FONTS.medium, color: '#DC2626', textAlign: isRTL ? 'right' : 'left', marginTop: 2 }}>
                    {isRTL
                      ? isSpecialization
                        ? '⚠ مجموع أوزان المؤشرات والمشاريع والأنشطة يتجاوز 100%'
                        : '⚠ مجموع الأوزان يتجاوز 100%'
                      : isSpecialization
                        ? '⚠ Total weight of KPIs, projects, and activities exceeds 100%'
                        : '⚠ Total weight exceeds 100%'}
                  </Text>
                )}
                {isSpecialization && weightItems.length > 0 && (
                  <TouchableOpacity
                    onPress={openWeightModal}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 6, alignSelf: isRTL ? 'flex-end' : 'flex-start' }}
                  >
                    <Svg width={14} height={14} viewBox="0 0 24 24" fill="none">
                      <Path d="M12 3v18M3 12h18" stroke="#A9211F" strokeWidth={2} strokeLinecap="round" />
                    </Svg>
                    <Text style={{ fontSize: 12, fontFamily: FONTS.medium, color: '#A9211F' }}>
                      {isRTL ? 'توزيع الأوزان' : 'Distribute Weights'}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>

              {/* KPI Name Field */}
              <View style={styles.fieldLarge}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'اسم المؤشر' : 'KPI Name'}
                  </Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.name ? styles.inputError : undefined]}
                  value={formData.name}
                  onChangeText={(v) => updateField('name', v)}
                  placeholder=""
                  placeholderTextColor="#9CA3AF"
                  textAlign="right"
                />
                {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
              </View>
            </View>

            {/* Description Field */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={styles.requiredStar}>*</Text>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {isRTL ? 'وصف المؤشر' : 'KPI Description'}
                </Text>
              </View>
              <TextInput
                style={[styles.input, styles.textArea, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }, errors.description ? styles.inputError : undefined]}
                value={formData.description}
                onChangeText={(v) => updateField('description', v)}
                placeholder=""
                placeholderTextColor="#9CA3AF"
                textAlign="right"
                multiline
                numberOfLines={3}
              />
              {errors.description && <Text style={styles.errorText}>{errors.description}</Text>}
            </View>

            {/* Responsible Field */}
            <View style={styles.field}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {isRTL ? 'المسؤول' : 'Responsible'}
                </Text>
              </View>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.responsible}
                onChangeText={(v) => updateField('responsible', v)}
                placeholder="حدد المسؤول عن المؤشر"
                placeholderTextColor="#9CA3AF"
                textAlign="right"
              />
            </View>

            {/* Overall / general KPI toggle — only when adding on a
                specialization (it fans out to that talent's enrolled students).
                Hidden when a KPI is added straight for one student. */}
            {entityInfo?.entityType === 'specialization' && (
              <View style={styles.toggleRow}>
                <View style={styles.toggleTextWrap}>
                  <Text style={[styles.toggleLabel, { color: themeColors.label }]}>
                    {isRTL ? 'مؤشر عام' : 'Public KPI'}
                  </Text>
                  <Text style={[styles.toggleDescription, { color: themeColors.placeholder }]}>
                    {isRTL
                      ? 'مؤشر عام (يتم تطبيق المؤشر على جميع الطلاب المندرجين تحت الموهبة، يحسب الإنجاز بشكل إفرادي لكل طالب ويظهر المتوسط العام للإنجاز)'
                      : 'Public KPI (Applied to all students under the specialization. Achievement is calculated individually per student and the overall average is displayed.)'}
                  </Text>
                </View>
                <TouchableOpacity
                  activeOpacity={0.8}
                  onPress={() => setFormData(prev => ({ ...prev, isPublicKPI: !prev.isPublicKPI }))}
                  style={[
                    styles.customToggleTrack,
                    { backgroundColor: formData.isPublicKPI ? '#A9211F' : '#D1D5DB' },
                  ]}
                >
                  <View style={[
                    styles.customToggleThumb,
                    formData.isPublicKPI ? styles.customToggleThumbOn : styles.customToggleThumbOff,
                  ]} />
                </TouchableOpacity>
              </View>
            )}
          </View>

          {/* ============================================ */}
          {/* Section 2: Classification */}
          {/* ============================================ */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder, zIndex: 30 }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>
              {isRTL ? 'التصنيف' : 'Classification'}
            </Text>

            <View style={[styles.row, { zIndex: 20 }, isMobile && { flexDirection: 'column' }]}>
              {/* Polarity */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'اتجاه المؤشر (النمطية)' : 'Polarity'}
                  </Text>
                </View>
                <Dropdown
                  value={formData.polarity}
                  options={polarityOptions}
                  placeholder={isRTL ? 'اختر اتجاه المؤشر (تصاعدي، تنازلي)' : 'Select polarity (ascending, descending)'}
                  onChange={(v) => updateField('polarity', v)}
                />
                {errors.polarity && <Text style={styles.errorText}>{errors.polarity}</Text>}
              </View>

              {/* KPI Classification */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'تصنيف المؤشر' : 'KPI Classification'}
                  </Text>
                </View>
                <Dropdown
                  value={formData.kpiType}
                  options={kpiTypeOptions}
                  placeholder={isRTL ? 'اختر تصنيف المؤشر' : 'Select KPI classification'}
                  onChange={(v) => updateField('kpiType', v)}
                />
                {errors.kpiType && <Text style={styles.errorText}>{errors.kpiType}</Text>}
              </View>
            </View>
          </View>

          {/* ============================================ */}
          {/* Section 3: Measurement */}
          {/* ============================================ */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder, zIndex: 20 }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>
              {isRTL ? 'القياس' : 'Measurement'}
            </Text>

            {/* Row: Start Date + End Date */}
            <View style={[styles.row, isMobile && { flexDirection: 'column' }, { zIndex: 50 }]}>
              {/* End Date */}
              <View style={[styles.fieldHalf, { zIndex: 51 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'تاريخ التحقق (نهاية القياس)' : 'End Date'}
                  </Text>
                </View>
                <SingleDatePicker
                  value={formData.endDate}
                  onChange={(v) => updateField('endDate', v)}
                  minDate={formData.startDate || undefined}
                  placeholder={isRTL ? 'DD-MM-YYYY' : 'DD-MM-YYYY'}
                />
              </View>

              {/* Start Date */}
              <View style={[styles.fieldHalf, { zIndex: 52 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'تاريخ بداية القياس' : 'Start Date'}
                  </Text>
                </View>
                <SingleDatePicker
                  value={formData.startDate}
                  onChange={(v) => {
                    if (!firstStartDate) setFirstStartDate(v);
                    updateField('startDate', v);
                  }}
                  minDate={startDateMinDate}
                  placeholder={isRTL ? 'DD-MM-YYYY' : 'DD-MM-YYYY'}
                />
              </View>
            </View>

            {/* Row: Unit + Unit Description */}
            <View style={[styles.row, { zIndex: 100, position: 'relative' }, isMobile && { flexDirection: 'column' }]}>
              {/* Unit Description */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'وصف وحدة القياس' : 'Unit Description'}
                  </Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={formData.unitDescription}
                  onChangeText={(v) => updateField('unitDescription', v)}
                  placeholder="اكتب الوصف"
                  placeholderTextColor="#9CA3AF"
                  textAlign="right"
                />
              </View>

              {/* Unit */}
              <View style={[styles.fieldHalf, { zIndex: 2 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'وحدة القياس' : 'Unit'}
                  </Text>
                </View>
                <Dropdown
                  value={formData.unit}
                  options={unitOptions}
                  placeholder={isRTL ? 'اختر وحدة القياس(نسبة أو عدد)' : 'Select unit (percentage or count)'}
                  onChange={(v) => updateField('unit', v)}
                />
                {errors.unit && <Text style={styles.errorText}>{errors.unit}</Text>}
              </View>
            </View>

            {/* Row: Frequency + Data Source */}
            <View style={[styles.row, { zIndex: 90, position: 'relative' }, isMobile && { flexDirection: 'column' }]}>
              {/* Data Source */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'مصدر البيانات' : 'Data Source'}
                  </Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={formData.dataSource}
                  onChangeText={(v) => updateField('dataSource', v)}
                  placeholder="مصدر الحصول على بيانات المؤشر(تقرير، نظام، قسم)"
                  placeholderTextColor="#9CA3AF"
                  textAlign="right"
                />
              </View>

              {/* Frequency */}
              <View style={[styles.fieldHalf, { zIndex: 2 }]}>
                <View style={styles.labelRow}>
                  <Text style={styles.requiredStar}>*</Text>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'دورية القياس' : 'Frequency'}
                  </Text>
                </View>
                <Dropdown
                  value={formData.frequency}
                  options={frequencyOptions}
                  placeholder={isRTL ? 'اختر دورية القياس' : 'Select frequency'}
                  onChange={(v) => updateField('frequency', v)}
                />
                {errors.frequency && <Text style={styles.errorText}>{errors.frequency}</Text>}
              </View>
            </View>

            {/* Formula Field */}
            <View style={[styles.field, { zIndex: 1 }]}>
              <View style={styles.labelRow}>
                <Text style={[styles.label, { color: themeColors.label }]}>
                  {isRTL ? 'معادلة القياس' : 'Measurement Formula'}
                </Text>
              </View>
              <TextInput
                style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                value={formData.formula}
                onChangeText={(v) => updateField('formula', v)}
                placeholder="الصيغة الرياضية أو الوصف الدقيق لكيفية حساب المؤشر"
                placeholderTextColor="#9CA3AF"
                textAlign="right"
              />
            </View>

            {/* Row: Baseline + Base Year */}
            <View style={[styles.row, { zIndex: 80, position: 'relative' }, isMobile && { flexDirection: 'column' }]}>
              {/* Base Year */}
              <View style={[styles.fieldHalf, { zIndex: 2 }]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'سنة الأساس' : 'Base Year'}
                  </Text>
                </View>
                <Dropdown
                  value={formData.baseYear}
                  options={baseYearOptions}
                  placeholder={isRTL ? 'اختر السنة' : 'Select year'}
                  onChange={(v) => updateField('baseYear', v)}
                />
              </View>

              {/* Baseline */}
              <View style={styles.fieldHalf}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'خط الأساس للمستهدف' : 'Target Baseline'}
                  </Text>
                </View>
                <TextInput
                  style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                  value={formData.baseline}
                  onChangeText={(v) => updateField('baseline', normalizeArabicNumbers(v))}
                  keyboardType="numeric"
                  placeholder="0.0"
                  placeholderTextColor="#9CA3AF"
                  textAlign="right"
                />
              </View>
            </View>

            {/* Row: Target Pattern + Aggregation Method */}
            <View style={[styles.row, { zIndex: 70, position: 'relative' }, isMobile && { flexDirection: 'column' }]}>
              {/* Aggregation Method */}
              <View style={[styles.fieldHalf, { zIndex: 2 }]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'طريقة التجميع للمستهدف السنوي' : 'Aggregation Method'}
                  </Text>
                </View>
                <Dropdown
                  value={formData.aggregationMethod}
                  options={aggregationMethodOptions}
                  placeholder={isRTL ? 'مجموع القيم' : 'Sum of values'}
                  onChange={(v) => updateField('aggregationMethod', v)}
                />
              </View>

              {/* Target Pattern */}
              <View style={[styles.fieldHalf, { zIndex: 2 }]}>
                <View style={styles.labelRow}>
                  <Text style={[styles.label, { color: themeColors.label }]}>
                    {isRTL ? 'نمط تكرار أو تغير المستهدف' : 'Target Pattern'}
                  </Text>
                </View>
                <Dropdown
                  value={formData.targetPattern}
                  options={targetPatternOptions}
                  placeholder={isRTL ? 'اختر النمط' : 'Select pattern'}
                  onChange={(v) => updateField('targetPattern', v)}
                />
              </View>
            </View>

            {/* Pattern step inputs — only for increase/decrease patterns.
                These drive the auto-generated per-period target grid on the
                backend (first value + step across periods). */}
            {isStepPattern && (
              <View style={[styles.row, isMobile && { flexDirection: 'column' }]}>
                <View style={styles.fieldHalf}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.label, { color: themeColors.label }]}>
                      {isRTL ? 'القيمة المستهدفة للقراءة الأولى' : 'First reading target'}
                    </Text>
                  </View>
                  <TextInput
                    style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                    value={formData.targetFirstValue}
                    onChangeText={(v) => updateField('targetFirstValue', normalizeArabicNumbers(v))}
                    placeholder="0.0"
                    placeholderTextColor="#9CA3AF"
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
                <View style={styles.fieldHalf}>
                  <View style={styles.labelRow}>
                    <Text style={[styles.label, { color: themeColors.label }]}>
                      {isPctPattern
                        ? (isRTL ? 'نسبة التزايد / التناقص (%)' : 'Step percent (%)')
                        : (isRTL ? 'قيمة التزايد / التناقص' : 'Step amount')}
                    </Text>
                  </View>
                  <TextInput
                    style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                    value={isPctPattern ? formData.targetStepPercent : formData.targetStepAmount}
                    onChangeText={(v) => updateField(isPctPattern ? 'targetStepPercent' : 'targetStepAmount', normalizeArabicNumbers(v))}
                    placeholder="0.0"
                    placeholderTextColor="#9CA3AF"
                    keyboardType="numeric"
                    textAlign="right"
                  />
                </View>
              </View>
            )}
          </View>

          {/* ============================================ */}
          {/* Section 4: Annual Targets */}
          {/* ============================================ */}
          <View style={[styles.section, { backgroundColor: themeColors.section, borderColor: themeColors.sectionBorder }]}>
            <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>
              {isRTL ? 'القيم المستهدفة سنوياً' : 'Annual Targets'}
            </Text>

            {targetYears.length === 0 && (
              <Text style={{ textAlign: 'center', color: themeColors.placeholder, fontFamily: FONTS.regular, fontSize: 13, paddingVertical: 16 }}>
                {isRTL ? 'يرجى تحديد تاريخ بداية ونهاية القياس أولاً' : 'Please set measurement start and end dates first'}
              </Text>
            )}

            {targetYears.map((yr) => (
              <View key={yr} style={[styles.annualTargetRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                <Text style={[styles.yearLabel, { color: themeColors.label }]}>{yr}</Text>
                <View style={[styles.row, { flex: 1, marginBottom: 0 }, isMobile && { flexDirection: 'column' }]}>
                  <View style={styles.fieldHalf}>
                    <View style={styles.labelRow}>
                      <Text style={[styles.label, { color: themeColors.label }]}>
                        {isRTL ? 'مستهدف سنة القياس' : 'Year Target'}
                      </Text>
                    </View>
                    <TextInput
                      style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                      value={annualTargets[yr]?.year || ''}
                      onChangeText={(v) => setAnnualTargets(prev => ({ ...prev, [yr]: { ...prev[yr], year: normalizeArabicNumbers(v) } }))}
                      keyboardType="numeric"
                      textAlign="right"
                    />
                  </View>
                  <View style={styles.fieldHalf}>
                    <View style={styles.labelRow}>
                      <Text style={styles.requiredStar}>*</Text>
                      <Text style={[styles.label, { color: themeColors.label }]}>
                        {isRTL ? 'القيمة المستهدفة للقراءة' : 'Reading Target'}
                      </Text>
                    </View>
                    <TextInput
                      style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                      value={annualTargets[yr]?.reading || ''}
                      onChangeText={(v) => setAnnualTargets(prev => ({ ...prev, [yr]: { ...prev[yr], reading: normalizeArabicNumbers(v) } }))}
                      keyboardType="numeric"
                      textAlign="right"
                    />
                  </View>
                </View>
              </View>
            ))}

            {/* Total Target (computed, read-only) */}
            {targetYears.length > 0 && (
              <View style={[styles.totalTargetRow, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
                <View style={styles.totalYearsBadge}>
                  <Text style={styles.totalYearsBadgeText}>
                    {targetYears.length > 1
                      ? `${targetYears[0]}-${targetYears[targetYears.length - 1]}`
                      : String(targetYears[0])}
                  </Text>
                </View>
                <View style={[styles.row, { flex: 1, marginBottom: 0 }, isMobile && { flexDirection: 'column' }]}>
                  <View style={styles.fieldHalf}>
                    <Text style={[styles.totalTargetLabel, { color: themeColors.label }]}>
                      {isRTL ? 'المستهدف الكلي' : 'Total Target'}
                    </Text>
                    <TextInput
                      style={[styles.input, { backgroundColor: themeColors.input, borderColor: themeColors.inputBorder, color: themeColors.inputText }]}
                      value={computedTotalTarget > 0 ? String(computedTotalTarget) : ''}
                      editable={false}
                      textAlign="right"
                    />
                  </View>
                  <View style={styles.fieldHalf} />
                </View>
              </View>
            )}
          </View>

          {/* ============================================ */}
          {/* Action Buttons */}
          {/* ============================================ */}
          <View style={styles.actionButtons}>
            <TouchableOpacity
              style={[styles.saveButton, loading && styles.saveButtonDisabled]}
              onPress={handleSave}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.saveButtonText}>
                  {isRTL ? 'حفظ' : 'Save'}
                </Text>
              )}
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.cancelButton, { backgroundColor: themeColors.cancelButton, borderColor: themeColors.cancelButtonBorder }]}
              onPress={handleCancel}
              disabled={loading}
            >
              <Text style={[styles.cancelButtonText, { color: themeColors.cancelButtonText }]}>
                {isRTL ? 'إلغاء' : 'Cancel'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>

      {/* Weight Distribution Modal */}
      <Modal visible={showWeightModal} transparent animationType="fade" onRequestClose={() => setShowWeightModal(false)}>
        <Pressable style={wdStyles.overlay} onPress={() => setShowWeightModal(false)}>
          <Pressable style={wdStyles.container} onPress={(e) => e.stopPropagation()}>
            {/* Header */}
            <View style={wdStyles.header}>
              <TouchableOpacity onPress={() => setShowWeightModal(false)} style={wdStyles.closeBtn}>
                <Text style={{ fontSize: 20, color: '#64748B' }}>×</Text>
              </TouchableOpacity>
              <Text style={wdStyles.title}>{isRTL ? 'توزيع الأوزان' : 'Weight Distribution'}</Text>
            </View>

            {/* Specialization name */}
            {specName ? <Text style={wdStyles.specName}>{specName}</Text> : null}

            {/* Description */}
            <Text style={wdStyles.description}>
              {isRTL
                ? 'توزيع أوزان العناصر الفرعية على العنصر الأصل المرتبطة به'
                : 'Distribute sub-item weights across the parent element'}
            </Text>

            {/* Info note */}
            <View style={wdStyles.infoRow}>
              <Text style={wdStyles.infoIcon}>ⓘ</Text>
              <Text style={wdStyles.infoText}>
                {isRTL
                  ? 'يجب أن يتم توزيع الوزن الكلي بشكل كامل بحيث يكون الباقي "0" حتى يتم قبول التوزيع'
                  : 'Total weight must be fully distributed (remaining = 0) for the distribution to be accepted'}
              </Text>
            </View>

            {/* Weight rows */}
            <ScrollView style={{ maxHeight: 320 }}>
              {/* Current KPI being added */}
              <View style={wdStyles.row}>
                <View style={wdStyles.rowWeight}>
                  <Text style={wdStyles.rowWeightLabel}>{isRTL ? 'الوزن' : 'Weight'}</Text>
                  <Text style={wdStyles.rowWeightValue}>{Math.round(currentWeight)}</Text>
                  <Text style={wdStyles.rowPercent}>%</Text>
                </View>
                <View style={wdStyles.rowNameBox}>
                  <Text style={wdStyles.rowName} numberOfLines={1}>
                    {formData.name || (isRTL ? 'المؤشر الحالي' : 'Current KPI')}{isRTL ? ' (مؤشر أداء) ★' : ' (KPI) ★'}
                  </Text>
                </View>
              </View>

              {/* Existing items */}
              {weightItems.map((item, i) => {
                const key = `${item.type}-${item.id}-${i}`;
                return (
                  <View key={key} style={wdStyles.row}>
                    <View style={wdStyles.rowWeight}>
                      <Text style={wdStyles.rowWeightLabel}>{isRTL ? 'الوزن' : 'Weight'}</Text>
                      <TextInput
                        style={wdStyles.rowWeightInput}
                        value={modalWeights[key] ?? String(item.weight)}
                        onChangeText={(v) => setModalWeights(prev => ({ ...prev, [key]: normalizeArabicNumbers(v) }))}
                        keyboardType="numeric"
                        textAlign="center"
                      />
                      <Text style={wdStyles.rowPercent}>%</Text>
                    </View>
                    <View style={wdStyles.rowNameBox}>
                      <Text style={wdStyles.rowName} numberOfLines={1}>{item.name}</Text>
                    </View>
                  </View>
                );
              })}
            </ScrollView>

            {/* Remaining */}
            <View style={[wdStyles.remainingRow, modalRemaining < 0 && { borderColor: '#DC2626' }]}>
              <View style={wdStyles.rowWeight}>
                <Text style={[wdStyles.remainingValue, modalRemaining !== 0 && { color: '#DC2626' }]}>
                  {Math.round(modalRemaining)}
                </Text>
                <Text style={[wdStyles.rowPercent, modalRemaining !== 0 && { color: '#DC2626' }]}>%</Text>
              </View>
              <Text style={[wdStyles.remainingLabel, modalRemaining !== 0 && { color: '#DC2626' }]}>
                {isRTL ? 'الباقي' : 'Remaining'}
              </Text>
            </View>

            {/* Auto redistribute */}
            <TouchableOpacity onPress={autoRedistribute} style={wdStyles.autoBtn}>
              <Text style={wdStyles.autoBtnText}>{isRTL ? '⟲ أعادة توزيع تلقائي' : '⟲ Auto Redistribute'}</Text>
            </TouchableOpacity>

            {/* Footer buttons */}
            <View style={wdStyles.footer}>
              <TouchableOpacity
                onPress={handleSaveWeights}
                disabled={savingWeights || modalRemaining < 0}
                style={[wdStyles.saveBtn, (savingWeights || modalRemaining < 0) && { opacity: 0.5 }]}
              >
                {savingWeights
                  ? <ActivityIndicator size="small" color="#FFFFFF" />
                  : <Text style={wdStyles.saveBtnText}>{isRTL ? 'حفظ' : 'Save'}</Text>}
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setShowWeightModal(false)} style={wdStyles.cancelModalBtn}>
                <Text style={wdStyles.cancelModalBtnText}>{isRTL ? 'إلغاء' : 'Cancel'}</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </PageContainer>
  );
}

// ============================================================
// Weight Distribution Modal Styles
// ============================================================
const wdStyles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 520,
    maxHeight: '85%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: '#171717',
    textAlign: 'right',
    flex: 1,
  },
  specName: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#171717',
    textAlign: 'center',
    marginBottom: 8,
    fontWeight: '700',
  },
  description: {
    fontSize: 13,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 8,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 6,
    backgroundColor: '#F8FAFC',
    borderRadius: 8,
    padding: 10,
    marginBottom: 16,
    justifyContent: 'flex-end',
  },
  infoIcon: {
    fontSize: 14,
    color: '#94A3B8',
  },
  infoText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#64748B',
    textAlign: 'right',
    flex: 1,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  rowWeight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  rowWeightLabel: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#94A3B8',
  },
  rowWeightValue: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    minWidth: 30,
    textAlign: 'center',
  },
  rowWeightInput: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 4,
    minWidth: 48,
    textAlign: 'center',
  },
  rowPercent: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#94A3B8',
  },
  rowNameBox: {
    flex: 1,
    backgroundColor: '#F1F5F9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  rowName: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  remainingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
    marginBottom: 16,
    justifyContent: 'flex-end',
  },
  remainingLabel: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#22C55E',
  },
  remainingValue: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#22C55E',
  },
  autoBtn: {
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignSelf: 'flex-start',
    marginBottom: 20,
  },
  autoBtnText: {
    fontSize: 13,
    fontFamily: FONTS.medium,
    color: '#414042',
  },
  footer: {
    flexDirection: 'row',
    gap: 12,
  },
  saveBtn: {
    backgroundColor: '#A9211F',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    minWidth: 80,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveBtnText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  cancelModalBtn: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelModalBtnText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
});

// ============================================================
// Styles
// ============================================================

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: 24,
  },
  // Main Card
  mainCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    elevation: 2,
    overflow: 'visible',
  },
  // Page Title
  pageTitle: {
    fontSize: 18,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 24,
  },
  // Section
  section: {
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    overflow: 'visible',
  },
  sectionTitle: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 16,
  },
  // Field styles
  field: {
    marginBottom: 16,
  },
  fieldSmall: {
    width: '30%',
  },
  fieldLarge: {
    flex: 1,
  },
  fieldHalf: {
    flex: 1,
    overflow: 'visible',
  },
  row: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 16,
    overflow: 'visible',
  },
  labelRow: {
    flexDirection: 'row-reverse',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 4,
    marginBottom: 8,
  },
  label: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  requiredStar: {
    fontSize: 14,
    color: '#EF4444',
  },
  // Input styles
  input: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
    writingDirection: 'rtl',
    minHeight: 44,
  },
  textArea: {
    minHeight: 80,
    textAlignVertical: 'top',
  },
  inputError: {
    borderColor: '#EF4444',
  },
  // Weight Input
  weightInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  weightInput: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  percentSymbol: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#64748B',
  },
  setWeightLabel: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  setWeightButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Toggle row (public KPI)
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 8,
    paddingVertical: 8,
  },
  toggleTextWrap: {
    flex: 1,
    alignItems: 'flex-end',
  },
  toggleLabel: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
    marginBottom: 4,
  },
  toggleDescription: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#9CA3AF',
    textAlign: 'right',
    lineHeight: 18,
  },
  customToggleTrack: {
    width: 48,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    paddingHorizontal: 3,
  },
  customToggleThumb: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#FFFFFF',
  },
  customToggleThumbOn: {
    alignSelf: 'flex-end',
  },
  customToggleThumbOff: {
    alignSelf: 'flex-start',
  },
  // Date Input
  dateInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    gap: 8,
  },
  dateInput: {
    flex: 1,
    paddingVertical: 10,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  // Annual Targets
  annualTargetRow: {
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  yearLabel: {
    fontSize: 16,
    fontFamily: FONTS.medium,
    color: '#414042',
    minWidth: 56,
    textAlign: 'center',
    backgroundColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 16,
    overflow: 'hidden',
  },
  totalTargetRow: {
    alignItems: 'center',
    gap: 12,
    marginTop: 8,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
  },
  totalYearsBadge: {
    minWidth: 56,
    backgroundColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  totalYearsBadgeText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'center',
  },
  totalTargetLabel: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#414042',
    textAlign: 'right',
  },
  totalTargetValue: {
    fontSize: 16,
    fontFamily: FONTS.numbers,
    fontWeight: '600',
    color: '#414042',
  },
  // Dropdown styles
  dropdownContainer: {
    position: 'relative',
    zIndex: 1,
  },
  dropdownButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    minHeight: 44,
  },
  dropdownButtonText: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownItem: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#FFFFFF',
  },
  dropdownItemText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
    textAlign: 'right',
  },
  dropdownItemTextActive: {
    color: '#A9211F',
    fontWeight: '600',
  },
  // Action Buttons
  actionButtons: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  saveButton: {
    backgroundColor: '#A9211F',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    minWidth: 80,
    minHeight: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  saveButtonDisabled: {
    backgroundColor: '#D1D5DB',
  },
  saveButtonText: {
    fontSize: 14,
    fontFamily: FONTS.medium,
    color: '#FFFFFF',
  },
  cancelButton: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 24,
    paddingVertical: 10,
    minHeight: 44,
    justifyContent: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    color: '#414042',
  },
  // Error styles
  errorText: {
    fontSize: 12,
    fontFamily: FONTS.regular,
    color: '#EF4444',
    textAlign: 'right',
    marginTop: 4,
  },
});
